/**
 * @module CheckPerformanceAnalyticsConsent
 *
 * Utility functions for checking if the user has provided consent for performance/analytics cookies.
 *
 * This module provides a function to determine if the PERFORMANCE_ANALYTICS_COOKIE_LABEL is present in the user's accepted cookies.
 *
 * Usage Example:
 * ```typescript
 * import { hasPerformanceAnalyticsConsent } from '@/modules/cookie-settings/CheckPerformanceAnalyticsConsent'
 *
 * if (hasPerformanceAnalyticsConsent()) {
 *   // Enable analytics
 * }
 * ```
 */
import { getCookies } from '@/utils/cookies/cookies'
import { PERFORMANCE_ANALYTICS_COOKIE_LABEL } from '@/modules/cookie-settings/cookieSettings.constants'

/**
 * Interface representing cookies with an optional acceptCookies property.
 */
interface CookiesWithAccept {
  [key: string]: unknown
  acceptCookies?: string | string[]
}

/**
 * Parses acceptCookies if it is an array.
 * @param {string[]} acceptCookiesArr - The acceptCookies value as an array.
 * @returns {string[]} The accepted cookies array.
 */
function parseAcceptCookiesArray(acceptCookiesArr: string[]): string[] {
  return acceptCookiesArr
}

/**
 * Parses acceptCookies if it is a string.
 * @param {string} acceptCookiesStr - The acceptCookies value as a string.
 * @returns {string[]} The accepted cookies array.
 */
function parseAcceptCookiesString(acceptCookiesStr: string): string[] {
  try {
    const decodedCookie: string = decodeURIComponent(acceptCookiesStr)
    const parsed: unknown = JSON.parse(decodedCookie)
    if (Array.isArray(parsed)) {
      return parsed
    }
    return decodedCookie.split(',')
  } catch {
    return acceptCookiesStr.split(',')
  }
}

/**
 * Parses the acceptCookies value from cookies and returns an array of accepted cookie labels.
 * Handles both string and array formats, including decoding and parsing JSON if needed.
 *
 * @param {string | string[]} acceptCookies - The acceptCookies value from cookies.
 * @returns {string[]} Array of accepted cookie labels.
 */
function parseAcceptedCookies(acceptCookies: string | string[]): string[] {
  if (Array.isArray(acceptCookies)) {
    return parseAcceptCookiesArray(acceptCookies)
  }
  if (typeof acceptCookies === 'string') {
    return parseAcceptCookiesString(acceptCookies)
  }
  return []
}

/**
 * Checks if the user has accepted performance/analytics cookies.
 *
 * This function retrieves cookies using the getCookies utility and checks if the PERFORMANCE_ANALYTICS_COOKIE_LABEL
 * is present in the user's accepted cookies. It handles both string and array formats for the acceptCookies property.
 *
 * @returns {boolean} True if PERFORMANCE_ANALYTICS_COOKIE_LABEL is present in acceptCookies, false otherwise.
 */
function hasPerformanceAnalyticsConsent(): boolean {
  const cookies: CookiesWithAccept = getCookies() as CookiesWithAccept
  if (!cookies.acceptCookies) {
    return false
  }
  try {
    const accepted: string[] = parseAcceptedCookies(cookies.acceptCookies)
    // Return false if PERFORMANCE_ANALYTICS_COOKIE_LABEL is not present in accepted
    if (
      !accepted
        .map(trimCookieLabel)
        .includes(PERFORMANCE_ANALYTICS_COOKIE_LABEL)
    ) {
      return false
    }
    return true
  } catch {
    return false
  }
}

/**
 * Trims whitespace from a cookie label.
 *
 * @param {string} label - The cookie label to trim.
 * @returns {string} The trimmed label.
 */
function trimCookieLabel(label: string): string {
  return label.trim()
}

export { hasPerformanceAnalyticsConsent }
